import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaUserShield, FaGraduationCap,
         FaChevronDown, FaChevronUp, FaSave, FaTimes, FaUserPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';
import cpApi from '../../utils/cpApi';
import './AddSuperAdmin.css';

const EMPTY_FORM = { name: '', email: '', password: '' };

const AddSuperAdmin = () => {
  const [users, setUsers]             = useState([]);
  const [allAdmins, setAllAdmins]     = useState([]);
  const [colleges, setColleges]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [editId, setEditId]           = useState(null);
  const [expandedInfo, setExpandedInfo] = useState(null);

  // Assignment panel
  const [assignId, setAssignId]                       = useState(null);
  const [tempColleges, setTempColleges]               = useState([]);
  const [originalColleges, setOriginalColleges]       = useState([]);
  const [collegeSearch, setCollegeSearch]             = useState('');
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [adminSearch, setAdminSearch]                 = useState('');
  const [showAdminDropdown, setShowAdminDropdown]     = useState(false);

  const collegeDropdownRef = useRef(null);
  const adminDropdownRef   = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, cRes, aRes] = await Promise.all([
        cpApi.get('/controlpanel/superadmin'),
        cpApi.get('/controlpanel/colleges'),
        cpApi.get('/controlpanel/admin'),
      ]);
      setUsers(uRes.data.data);
      setColleges(cRes.data.data.filter(c => c.isActive));
      setAllAdmins(aRes.data.data);
    } catch {
      // handle gracefully
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropdownRef.current && !collegeDropdownRef.current.contains(e.target))
        setShowCollegeDropdown(false);
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target))
        setShowAdminDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!form.name || !form.email) {
      Swal.fire('Missing Fields', 'Please fill Name and Email.', 'warning');
      return false;
    }
    if (!editId && !form.password) {
      Swal.fire('Missing Fields', 'Password is required for new super admins.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditId(null); setShowAddForm(false); };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password) payload.password = form.password;
      if (editId) {
        const { data } = await cpApi.put(`/controlpanel/superadmin/${editId}`, payload);
        setUsers(prev => prev.map(u => u._id === editId ? data.data : u));
        Swal.fire('Updated!', 'Super admin updated successfully.', 'success');
      } else {
        const { data } = await cpApi.post('/controlpanel/superadmin', payload);
        setUsers(prev => [data.data, ...prev]);
        Swal.fire('Added!', 'Super admin created successfully.', 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save.', 'error');
    } finally { setSaving(false); }
  };

  const handleEdit = (user) => {
    setEditId(user._id);
    setForm({ name: user.name, email: user.email, password: '' });
    setShowAddForm(true);
    setExpandedInfo(null);
    setAssignId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (user) => {
    Swal.fire({
      title: `Delete "${user.name}"?`, text: 'This cannot be undone.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e74c3c', cancelButtonColor: '#95a5a6',
      confirmButtonText: 'Yes, delete',
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await cpApi.delete(`/controlpanel/superadmin/${user._id}`);
        setUsers(prev => prev.filter(u => u._id !== user._id));
        if (expandedInfo === user._id) setExpandedInfo(null);
        if (assignId === user._id) setAssignId(null);
        Swal.fire('Deleted!', 'Super admin deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  const toggleStatus = async (user) => {
    try {
      const { data } = await cpApi.patch(`/controlpanel/superadmin/${user._id}/toggle`);
      setUsers(prev => prev.map(u => u._id === user._id ? data.data : u));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
    if (dragIndex === dropIndex) return;

    const newColleges = [...tempColleges];
    const draggedItem = newColleges[dragIndex];
    newColleges.splice(dragIndex, 1);
    newColleges.splice(dropIndex, 0, draggedItem);
    setTempColleges(newColleges);
  };
  const mergeColleges = (existing, incoming) => {
    const map = new Map(existing.map(c => [c._id, c]));
    incoming.forEach(c => { if (!map.has(c._id)) map.set(c._id, c); });
    return Array.from(map.values());
  };

  const openAssign = (user) => {
    setAssignId(user._id);
    const unique = Array.from(
      new Map((user.colleges || []).map(c => [c._id, { _id: c._id, name: c.name, code: c.code }])).values()
    );
    setTempColleges(unique);
    setOriginalColleges(unique);
    setCollegeSearch('');
    setAdminSearch('');
    setShowCollegeDropdown(false);
    setShowAdminDropdown(false);
    setExpandedInfo(null);
  };

  const closeAssign = async () => {
    const hasChanges =
      JSON.stringify(tempColleges.map(c => c._id).sort()) !==
      JSON.stringify(originalColleges.map(c => c._id).sort());
    if (hasChanges) {
      const r = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved college assignments. Discard them?',
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: '#e74c3c', cancelButtonColor: '#3498db',
        confirmButtonText: 'Yes, discard', cancelButtonText: 'Keep editing',
      });
      if (!r.isConfirmed) return;
    }
    setAssignId(null);
    setTempColleges([]);
    setOriginalColleges([]);
    setCollegeSearch('');
    setAdminSearch('');
    setShowCollegeDropdown(false);
    setShowAdminDropdown(false);
  };

  const addCollegeToTemp = (col) => {
    setTempColleges(prev => mergeColleges(prev, [{ _id: col._id, name: col.name, code: col.code }]));
    setCollegeSearch('');
    setShowCollegeDropdown(false);
  };

  const addAdminCollegesToTemp = (admin) => {
    const adminColleges = (admin.colleges || []).map(c => ({ _id: c._id, name: c.name, code: c.code }));
    if (adminColleges.length === 0) {
      Swal.fire('No Colleges', `"${admin.name}" has no assigned colleges.`, 'info');
    } else {
      setTempColleges(prev => mergeColleges(prev, adminColleges));
    }
    setAdminSearch('');
    setShowAdminDropdown(false);
  };

  const removeCollegeFromTemp = async (col) => {
    const wasSaved = originalColleges.find(c => c._id === col._id);
    if (wasSaved) {
      const r = await Swal.fire({
        title: `Remove "${col.name}"?`,
        text: 'This college was previously assigned. Removing it will unassign it when you save.',
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: '#e74c3c', cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, remove', cancelButtonText: 'Keep',
      });
      if (!r.isConfirmed) return;
    }
    setTempColleges(prev => prev.filter(c => c._id !== col._id));
  };

  const saveAssignment = async (id) => {
    try {
      const { data } = await cpApi.put(`/controlpanel/superadmin/${id}`, {
        colleges: tempColleges.map(c => c._id),
      });
      setUsers(prev => prev.map(u => u._id === id ? data.data : u));
      setAssignId(null);
      setTempColleges([]);
      setOriginalColleges([]);
      Swal.fire('Saved!', 'College assignments updated.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save assignments.', 'error');
    }
  };

  // ── Filters ──────────────────────────────────────────────────────────────────
  const tempIds = useMemo(() => new Set(tempColleges.map(c => c._id)), [tempColleges]);

  const availableColleges = useMemo(() => {
    const q = collegeSearch.toLowerCase();
    // Filter to only show colleges NOT in tempColleges
    return colleges.filter(c =>
      !tempIds.has(c._id) &&
      (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
    );
  }, [colleges, tempIds, collegeSearch]);

  const availableAdmins = useMemo(() => {
    const q = adminSearch.toLowerCase();
    return allAdmins.filter(a =>
      (a.colleges?.length > 0) &&
      (a.name.toLowerCase().includes(q) || a.userId?.toLowerCase().includes(q))
    );
  }, [allAdmins, adminSearch]);

  const displayedUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      u.name.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  return (
    <div className="add-super-admin-in-control-panel-container">
      <h2 className="add-super-admin-in-control-panel-title">Manage Super Admins</h2>

      {/* Controls */}
      <div className="add-super-admin-in-control-panel-controls">
        <div className="add-super-admin-in-control-panel-search-wrapper">
          <FaSearch className="add-super-admin-in-control-panel-search-icon" />
          <input
            type="text"
            className="add-super-admin-in-control-panel-search-input"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className="add-super-admin-in-control-panel-btn add-super-admin-in-control-panel-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}
        >
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Super Admin'}
        </button>
      </div>

      {/* Add / Edit Form */}
      {showAddForm && (
        <div className="add-super-admin-in-control-panel-inline-form">
          <h3 className="add-super-admin-in-control-panel-form-title">
            <FaUserShield style={{ marginRight: 8, color: '#3498db' }} />
            {editId ? 'Edit Super Admin' : 'Create New Super Admin'}
          </h3>
          <div className="add-super-admin-in-control-panel-form-grid">
            <div className="add-super-admin-in-control-panel-form-group">
              <label>Full Name <span className="add-super-admin-in-control-panel-req">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleInputChange} placeholder="e.g. Jane Doe" />
            </div>
            <div className="add-super-admin-in-control-panel-form-group">
              <label>Email <span className="add-super-admin-in-control-panel-req">*</span></label>
              <input type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="e.g. jane@example.com" />
            </div>
            <div className="add-super-admin-in-control-panel-form-group">
              <label>
                Password
                {editId
                  ? <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>(leave blank to keep)</span>
                  : <span className="add-super-admin-in-control-panel-req"> *</span>}
              </label>
              <input
                type="password" name="password" value={form.password} onChange={handleInputChange}
                placeholder={editId ? 'New password to change' : 'Enter password'}
              />
            </div>
          </div>
          <div className="add-super-admin-in-control-panel-form-buttons">
            <button className="add-super-admin-in-control-panel-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-super-admin-in-control-panel-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Super Admin' : 'Create Super Admin'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="add-super-admin-in-control-panel-loading">Loading Super Admins...</div>
      ) : (
        <div className="add-super-admin-in-control-panel-table-wrapper">
          <table className="add-super-admin-in-control-panel-table">
            <thead>
              <tr>
                <th className="add-super-admin-in-control-panel-col-sno">S.No</th>
                <th className="add-super-admin-in-control-panel-align-left">Name</th>
                <th className="add-super-admin-in-control-panel-col-adminid">Email</th>
                <th className="add-super-admin-in-control-panel-col-assigned">Assigned</th>
                <th className="add-super-admin-in-control-panel-col-status">Status</th>
                <th className="add-super-admin-in-control-panel-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr><td colSpan="6" className="add-super-admin-in-control-panel-empty-row">No super admins found.</td></tr>
              ) : displayedUsers.map((user, i) => {
                const uniqueCount = new Set((user.colleges || []).map(c => c._id)).size;
                return (
                  <React.Fragment key={user._id}>

                    <tr className={`add-super-admin-in-control-panel-main-row ${
                      expandedInfo === user._id || assignId === user._id
                        ? 'add-super-admin-in-control-panel-row-active' : ''
                    }`}>
                      <td className="add-super-admin-in-control-panel-col-sno">{i + 1}</td>

                      <td className="add-super-admin-in-control-panel-align-left">
                        <div className="add-super-admin-in-control-panel-name-wrapper"
                             onClick={() => setExpandedInfo(p => p === user._id ? null : user._id)}>
                          <span>{user.name}</span>
                          <button className="add-super-admin-in-control-panel-expand-btn">
                            {expandedInfo === user._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>

                      <td className="add-super-admin-in-control-panel-col-adminid">
                        <span className="add-super-admin-in-control-panel-badge-id">{user.email}</span>
                      </td>

                      <td className="add-super-admin-in-control-panel-col-assigned">
                        {uniqueCount > 0
                          ? <span className="add-super-admin-in-control-panel-badge-count">
                              {uniqueCount} {uniqueCount === 1 ? 'College' : 'Colleges'}
                            </span>
                          : <span className="add-super-admin-in-control-panel-not-assigned">Not Assigned</span>}
                      </td>

                      <td className="add-super-admin-in-control-panel-col-status">
                        <label className="add-super-admin-in-control-panel-switch">
                          <input type="checkbox" checked={user.isActive} onChange={() => toggleStatus(user)} />
                          <span className="add-super-admin-in-control-panel-slider" />
                        </label>
                        <div className="add-super-admin-in-control-panel-status-label" style={{ color: user.isActive ? '#27ae60' : '#e74c3c' }}>
                          {user.isActive ? 'Active' : 'Disabled'}
                        </div>
                      </td>

                      <td className="add-super-admin-in-control-panel-col-actions">
                        <div className="add-super-admin-in-control-panel-action-btns">
                          <button
                            className={`add-super-admin-in-control-panel-btn-assign ${assignId === user._id ? 'active' : ''}`}
                            onClick={e => { e.stopPropagation(); assignId === user._id ? closeAssign() : openAssign(user); }}
                            title="Assign Colleges"
                          >
                            <FaGraduationCap />
                            <span className="add-super-admin-in-control-panel-btn-text">
                              {assignId === user._id ? 'Close' : 'Assign'}
                            </span>
                          </button>
                          <button className="add-super-admin-in-control-panel-btn-edit" onClick={() => handleEdit(user)} title="Edit"><FaEdit /></button>
                          <button className="add-super-admin-in-control-panel-btn-delete" onClick={() => handleDelete(user)} title="Delete"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>

                    {/* Expand info row */}
                    {expandedInfo === user._id && (
                      <tr className="add-super-admin-in-control-panel-info-row">
                        <td colSpan="6">
                          <div className="add-super-admin-in-control-panel-expanded-content">
                            <div className="add-super-admin-in-control-panel-expanded-grid">
                              <div className="add-super-admin-in-control-panel-exp-item add-super-admin-in-control-panel-exp-adminid">
                                <span className="add-super-admin-in-control-panel-exp-label">Email</span>
                                <span className="add-super-admin-in-control-panel-exp-value">
                                  <span className="add-super-admin-in-control-panel-badge-id">{user.email}</span>
                                </span>
                              </div>
                              <div className="add-super-admin-in-control-panel-exp-item add-super-admin-in-control-panel-exp-assigned">
                                <span className="add-super-admin-in-control-panel-exp-label">Assigned Colleges</span>
                                <span className="add-super-admin-in-control-panel-exp-value">
                                  {uniqueCount > 0
                                    ? <span className="add-super-admin-in-control-panel-badge-count">
                                        {uniqueCount} {uniqueCount === 1 ? 'College' : 'Colleges'}
                                      </span>
                                    : <span className="add-super-admin-in-control-panel-not-assigned">Not Assigned</span>}
                                </span>
                              </div>
                              <div className="add-super-admin-in-control-panel-exp-item add-super-admin-in-control-panel-exp-status">
                                <span className="add-super-admin-in-control-panel-exp-label">Status</span>
                                <span className="add-super-admin-in-control-panel-exp-value" style={{ color: user.isActive ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                                  {user.isActive ? 'Active' : 'Disabled'}
                                </span>
                              </div>
                              <div className="add-super-admin-in-control-panel-exp-item add-super-admin-in-control-panel-exp-actions">
                                <span className="add-super-admin-in-control-panel-exp-label">Actions</span>
                                <div className="add-super-admin-in-control-panel-action-btns" style={{ justifyContent: 'flex-start', marginTop: '4px' }}>
                                  <button
                                    className={`add-super-admin-in-control-panel-btn-assign ${assignId === user._id ? 'active' : ''}`}
                                    onClick={e => { e.stopPropagation(); assignId === user._id ? closeAssign() : openAssign(user); }}
                                  >
                                    <FaGraduationCap /> Assign
                                  </button>
                                  <button className="add-super-admin-in-control-panel-btn-edit" onClick={() => handleEdit(user)}><FaEdit /></button>
                                  <button className="add-super-admin-in-control-panel-btn-delete" onClick={() => handleDelete(user)}><FaTrash /></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Assignment panel */}
                    {assignId === user._id && (
                      <tr className="add-super-admin-in-control-panel-assign-row">
                        <td colSpan="6">
                          <div className="add-super-admin-in-control-panel-assignment-panel">

                            <div className="add-super-admin-in-control-panel-panel-header">
                              <div className="add-super-admin-in-control-panel-panel-title">
                                <FaGraduationCap style={{ color: '#3498db' }} />
                                <span>Assign Colleges to <strong>{user.name}</strong></span>
                                <span className="add-super-admin-in-control-panel-panel-count">{tempColleges.length} selected</span>
                              </div>
                              <div className="add-super-admin-in-control-panel-panel-actions">
                                <button className="add-super-admin-in-control-panel-btn-save" onClick={() => saveAssignment(user._id)}>
                                  <FaSave /> Save Assignments
                                </button>
                                <button className="add-super-admin-in-control-panel-btn-cancel-panel" onClick={closeAssign}>
                                  <FaTimes /> Cancel
                                </button>
                              </div>
                            </div>

                            <div className="add-super-admin-in-control-panel-assign-sources">

                              {/* Source 1: Import from Admin */}
                              <div className="add-super-admin-in-control-panel-source-block" ref={adminDropdownRef}>
                                <label className="add-super-admin-in-control-panel-search-label">
                                  <FaUserPlus style={{ marginRight: 5, color: '#7c3aed' }} />
                                  Import from Admin
                                </label>
                                <div className="add-super-admin-in-control-panel-search-box add-super-admin-in-control-panel-search-box-admin">
                                  <FaSearch className="add-super-admin-in-control-panel-search-icon" />
                                  <input
                                    type="text"
                                    placeholder="Search admin by name or ID..."
                                    value={adminSearch}
                                    onChange={e => { setAdminSearch(e.target.value); setShowAdminDropdown(true); }}
                                    onFocus={() => setShowAdminDropdown(true)}
                                  />
                                  {adminSearch && (
                                    <button className="add-super-admin-in-control-panel-search-clear"
                                      onClick={() => { setAdminSearch(''); setShowAdminDropdown(false); }}>
                                      <FaTimes />
                                    </button>
                                  )}
                                </div>
                                {showAdminDropdown && (
                                  <div className="add-super-admin-in-control-panel-dropdown">
                                    {availableAdmins.length > 0
                                      ? availableAdmins.map(a => (
                                        <div key={a._id} className="add-super-admin-in-control-panel-dropdown-item" onClick={() => addAdminCollegesToTemp(a)}>
                                          <div className="add-super-admin-in-control-panel-dropdown-name">{a.name}</div>
                                          <div className="add-super-admin-in-control-panel-dropdown-code">
                                            {a.colleges?.length || 0} college{a.colleges?.length !== 1 ? 's' : ''}
                                          </div>
                                        </div>
                                      ))
                                      : <div className="add-super-admin-in-control-panel-dropdown-empty">
                                          {adminSearch ? 'No matching admins.' : 'No admins with colleges found.'}
                                        </div>
                                    }
                                  </div>
                                )}
                              </div>

                              {/* Source 2: Add individual college */}
                              <div className="add-super-admin-in-control-panel-source-block" ref={collegeDropdownRef}>
                                <label className="add-super-admin-in-control-panel-search-label">
                                  <FaGraduationCap style={{ marginRight: 5, color: '#3498db' }} />
                                  Add Individual College
                                </label>
                                <div className="add-super-admin-in-control-panel-search-box">
                                  <FaSearch className="add-super-admin-in-control-panel-search-icon" />
                                  <input
                                    type="text"
                                    placeholder="Type college name or code..."
                                    value={collegeSearch}
                                    onChange={e => { setCollegeSearch(e.target.value); setShowCollegeDropdown(true); }}
                                    onFocus={() => setShowCollegeDropdown(true)}
                                  />
                                  {collegeSearch && (
                                    <button className="add-super-admin-in-control-panel-search-clear"
                                      onClick={() => { setCollegeSearch(''); setShowCollegeDropdown(false); }}>
                                      <FaTimes />
                                    </button>
                                  )}
                                </div>
                                {showCollegeDropdown && (
                                  <div className="add-super-admin-in-control-panel-dropdown">
                                    {availableColleges.length > 0
                                      ? availableColleges.map(col => (
                                        <div key={col._id} className="add-super-admin-in-control-panel-dropdown-item" onClick={() => addCollegeToTemp(col)}>
                                          <div className="add-super-admin-in-control-panel-dropdown-name">{col.name}</div>
                                          <div className="add-super-admin-in-control-panel-dropdown-code">{col.code}</div>
                                        </div>
                                      ))
                                      : <div className="add-super-admin-in-control-panel-dropdown-empty">
                                          {collegeSearch ? 'No matching active colleges.' : 'All active colleges are already assigned.'}
                                        </div>
                                    }
                                  </div>
                                )}
                              </div>

                            </div>

                            {/* Selected chips */}
                            <div className="add-super-admin-in-control-panel-selected-section">
                              <div className="add-super-admin-in-control-panel-selected-label">
                                Currently Assigned Colleges
                                {tempColleges.length > 0 && <span className="add-super-admin-in-control-panel-selected-count">{tempColleges.length}</span>}
                              </div>
                              {tempColleges.length > 0 ? (
                                <div className="add-super-admin-in-control-panel-chips">
                                  {tempColleges.map((col, index) => (
                                    <div 
                                      key={col._id} 
                                      className={`add-super-admin-in-control-panel-chip ${originalColleges.find(c => c._id === col._id) ? 'add-super-admin-in-control-panel-chip-saved' : 'add-super-admin-in-control-panel-chip-new'}`}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, index)}
                                      onDragOver={handleDragOver}
                                      onDrop={(e) => handleDrop(e, index)}
                                      style={{ cursor: 'move' }}
                                    >
                                      <div className="add-super-admin-in-control-panel-chip-number">{index + 1}</div>
                                      <div className="add-super-admin-in-control-panel-chip-info">
                                        <span className="add-super-admin-in-control-panel-chip-name">{col.name}</span>
                                        <span className="add-super-admin-in-control-panel-chip-code">{col.code}</span>
                                      </div>
                                      <button className="add-super-admin-in-control-panel-chip-remove" onClick={() => removeCollegeFromTemp(col)} title="Remove">
                                        <FaTimes />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="add-super-admin-in-control-panel-no-selection">
                                  No colleges assigned yet. Use the search fields above to add colleges.
                                </div>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AddSuperAdmin;